import styled, { css } from 'styled-components';

import colors from '../../assets/styles/variables/colors';

const mono = css`
  font-family: 'JetBrains Mono', ui-monospace, monospace;
`;

export const Page = styled.div`
  min-height: 100%;
  display: flex;
  flex-direction: column;
  background: ${colors.pageBg};
`;

export const TopNav = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.5rem;
  max-width: 72rem;
  margin: 0 auto;
  width: 100%;
`;

export const LogoLockup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const LogoImg = styled.img`
  height: 2.2rem;
  width: auto;
  border-radius: 6px;
`;

export const StarGithub = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: ${colors.yellow};
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.85;
  }
`;

export const Hero = styled.section`
  max-width: 72rem;
  margin: 0 auto;
  padding: 2rem 1.5rem 3rem;
  width: 100%;
`;

export const Eyebrow = styled.p`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${colors.textMuted};
  margin-bottom: 1rem;
`;

export const HeroTitle = styled.h1`
  font-size: clamp(2.75rem, 8vw, 4.5rem);
  font-weight: 800;
  line-height: 1.02;
  letter-spacing: -0.03em;
  color: ${colors.textOnDark};
  margin-bottom: 1rem;
`;

export const Hash = styled.span`
  color: ${colors.textMuted};
  font-weight: 700;
  margin-right: 0.15em;
`;

export const TitleAccent = styled.span`
  background: linear-gradient(90deg, ${colors.yellow} 0%, #fff8cc 45%, ${colors.blueLight} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

export const HeroLead = styled.p`
  font-size: clamp(1.125rem, 2.5vw, 1.35rem);
  line-height: 1.55;
  color: ${colors.textMuted};
  max-width: 32rem;

  strong {
    color: ${colors.textOnDark};
    font-weight: 600;
  }
`;

export const InstallWrap = styled.div`
  margin-top: 2rem;
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 0.75rem;
`;

export const InstallBox = styled.div`
  flex: 1 1 18rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border-radius: 12px;
  border: 1px solid ${colors.borderSubtle};
  background: ${colors.surface};
`;

export const InstallPrefix = styled.span`
  ${mono}
  font-size: 0.875rem;
  color: ${colors.auxiliar.success};
  flex-shrink: 0;
`;

export const InstallCode = styled.code`
  ${mono}
  font-size: 0.8125rem;
  color: ${colors.textOnDark};
  word-break: break-all;
`;

export const CopyBtn = styled.button`
  margin-left: auto;
  flex-shrink: 0;
  padding: 0.4rem 0.85rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  background: ${colors.yellow};
  color: ${colors.blue};
  transition: transform 0.12s ease, box-shadow 0.12s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 14px rgba(254, 209, 0, 0.35);
  }
`;

export const HeroActions = styled.div`
  margin-top: 1.25rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

export const BtnPrimary = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.65rem 1.35rem;
  border-radius: 999px;
  font-size: 0.875rem;
  font-weight: 700;
  background: ${colors.yellow};
  color: ${colors.blue};
  transition: transform 0.12s ease, box-shadow 0.12s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 18px rgba(254, 209, 0, 0.35);
  }
`;

export const BtnGhost = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.65rem 1.35rem;
  border-radius: 999px;
  font-size: 0.875rem;
  font-weight: 600;
  border: 1px solid ${colors.borderSubtle};
  color: ${colors.textOnDark};
  background: transparent;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: rgba(254, 209, 0, 0.45);
    background: rgba(255, 255, 255, 0.04);
  }
`;

/* ─── Download buttons ────────────────────────────────── */

export const DownloadRow = styled.div`
  margin-top: 1.5rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: flex-start;
`;

export const DlButtonWrap = styled.div`
  position: relative;
`;

export const DlButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem 1.25rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
  border: 1px solid ${colors.borderSubtle};
  color: ${colors.textOnDark};
  background: ${colors.surface};
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, transform 0.12s ease;
  white-space: nowrap;

  &:hover {
    border-color: rgba(254, 209, 0, 0.45);
    background: ${colors.surfaceRaised};
    transform: translateY(-1px);
  }
`;

export const DlIcon = styled.span`
  font-size: 1.1rem;
  line-height: 1;
`;

export const DlOsLabel = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.2;
`;

export const DlOsName = styled.span`
  font-weight: 700;
`;

export const DlOsVersion = styled.span`
  font-size: 0.7rem;
  color: ${colors.textMuted};
`;

export const DlChevron = styled.span`
  margin-left: 0.25rem;
  font-size: 0.65rem;
  color: ${colors.textMuted};
  transition: transform 0.15s ease;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0)')};
`;

export const DlDropdown = styled.div`
  position: absolute;
  top: calc(100% + 0.4rem);
  left: 0;
  min-width: max-content;
  background: ${colors.surfaceRaised};
  border: 1px solid ${colors.borderSubtle};
  border-radius: 10px;
  overflow: hidden;
  z-index: 20;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
`;

export const DlDropItem = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${colors.textOnDark};
  text-decoration: none;
  transition: background 0.12s ease;
  white-space: nowrap;

  &:hover {
    background: rgba(254, 209, 0, 0.08);
  }

  & + & {
    border-top: 1px solid ${colors.borderSubtle};
  }
`;

export const DlDropArch = styled.span`
  color: ${colors.textMuted};
  font-size: 0.75rem;
`;

export const BuildLabel = styled.p`
  margin-top: 1rem;
  margin-bottom: -0.75rem;
  font-size: 1.0625rem;
  line-height: 1.7;
  color: ${colors.textMuted};
`;

export const ExamplesSection = styled.section`
  max-width: 72rem;
  margin: 0 auto;
  padding: 0 1.5rem 3.5rem;
  width: 100%;
`;

export const ExamplesShell = styled.div`
  border-radius: 16px;
  border: 1px solid ${colors.borderSubtle};
  background: ${colors.surface};
  overflow: hidden;
  box-shadow: 0 24px 80px rgba(1, 0, 102, 0.2);
`;

export const ExamplesTop = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid ${colors.borderSubtle};
  background: ${colors.surfaceRaised};
`;

export const ExampleTabList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

export const ExampleTabBtn = styled.button`
  padding: 0.4rem 0.85rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
  border: 1px solid
    ${(p) => (p.$active ? colors.yellow : colors.borderSubtle)};
  background: ${(p) => (p.$active ? 'rgba(254, 209, 0, 0.12)' : 'transparent')};
  color: ${(p) => (p.$active ? colors.yellow : colors.textMuted)};
  transition:
    color 0.15s ease,
    border-color 0.15s ease,
    background 0.15s ease;

  &:hover {
    color: ${colors.textOnDark};
    border-color: rgba(254, 209, 0, 0.45);
  }
`;

export const ExamplesMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;

export const FileTab = styled.span`
  ${mono}
  font-size: 0.8125rem;
  color: ${colors.textMuted};
`;

export const SnippetCopyBtn = styled.button`
  padding: 0.4rem 0.85rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  background: ${colors.yellow};
  color: ${colors.blue};
  transition: transform 0.12s ease, box-shadow 0.12s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 14px rgba(254, 209, 0, 0.35);
  }
`;

export const ExamplesPanes = styled.div`
  display: grid;
  grid-template-columns: 1fr;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
    min-height: 280px;
  }
`;

export const EditorPane = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid ${colors.borderSubtle};

  @media (min-width: 768px) {
    border-bottom: none;
    border-right: 1px solid ${colors.borderSubtle};
  }
`;

export const PaneLabel = styled.div`
  padding: 0.4rem 0.75rem;
  font-size: 0.6875rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  color: ${colors.textMuted};
  background: rgba(0, 0, 0, 0.25);
`;

export const ExampleReadonly = styled.div`
  flex: 1;
  min-height: 220px;
  max-height: 420px;
  overflow: auto;
  background: ${colors.editorBg};

  pre {
    margin: 0;
    padding: 1rem 1rem 1.25rem;
    ${mono}
    font-size: 0.8125rem;
    line-height: 1.65;
  }

  code {
    display: block;
    white-space: pre;
  }

  .token.comment {
    color: ${colors.syntax.comment};
  }

  .token.string {
    color: ${colors.syntax.string};
  }

  .token.number {
    color: ${colors.syntax.number};
  }

  .token.keyword {
    color: ${colors.syntax.keyword};
  }

  .token.operator {
    color: ${colors.syntax.operator};
  }

  .token.punctuation {
    color: ${colors.syntax.operator};
  }
`;

export const OutputPane = styled.div`
  display: flex;
  flex-direction: column;
  background: ${colors.editorOutputBg};
`;

export const OutputBody = styled.pre`
  flex: 1;
  padding: 1rem 1rem 1.25rem;
  ${mono}
  font-size: 0.8125rem;
  line-height: 1.65;
  white-space: pre-wrap;
  word-break: break-word;
  color: ${colors.auxiliar.success};
`;

export const Section = styled.section`
  max-width: 72rem;
  margin: 0 auto;
  padding: 0 1.5rem 3rem;
  width: 100%;
`;

export const SectionTitle = styled.h2`
  font-size: clamp(1.75rem, 4vw, 2.25rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  color: ${colors.textOnDark};
  margin-bottom: 2rem;
`;

export const FeatureGrid = styled.ul`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  width: 100%;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    align-items: stretch;
  }
`;

export const FeatureCard = styled.li`
  padding: 1.5rem;
  border-radius: 16px;
  border: 1px solid ${colors.borderSubtle};
  background: ${colors.surface};
  min-width: 0;
`;

export const FeatureIcon = styled.div`
  font-size: 2rem;
  line-height: 1;
  margin-bottom: 1rem;
`;

export const FeatureTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 800;
  color: ${colors.textOnDark};
  margin-bottom: 0.5rem;
`;

export const FeatureText = styled.p`
  font-size: 0.9375rem;
  line-height: 1.55;
  color: ${colors.textMuted};
  max-width: 100%;
  overflow-wrap: break-word;
  word-wrap: break-word;

  strong {
    color: ${colors.yellow};
    font-weight: 700;
  }

  code {
    ${mono}
    font-size: 0.85em;
    color: ${colors.yellow};
  }
`;

export const FeatureSnippetStack = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  min-width: 0;
`;

export const FeatureSnippetBlock = styled.pre`
  margin: 0;
  max-height: 14rem;
  ${mono}
  font-size: 0.65rem;
  line-height: 1.4;
  padding: 0.55rem 0.65rem;
  border-radius: 8px;
  border: 1px solid ${colors.borderSubtle};
  background: rgba(0, 0, 0, 0.32);
  color: ${colors.text.light.medium};
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  max-width: 100%;
`;

export const ExceptionStack = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  min-width: 0;
`;

export const ExceptionLine = styled.p`
  margin: 0;
  ${mono}
  font-size: 0.65rem;
  line-height: 1.45;
  color: ${colors.text.light.little};
  padding: 0.5rem 0.65rem;
  border-radius: 8px;
  border: 1px solid ${colors.borderSubtle};
  background: rgba(0, 0, 0, 0.28);
  overflow-wrap: anywhere;
  word-break: break-word;
  max-width: 100%;
`;

export const ExName = styled.span`
  color: ${colors.yellow};
  font-weight: 700;
`;

export const MoreSyntaxLink = styled.a`
  display: inline-block;
  margin-top: 2rem;
  font-size: 0.8125rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  color: ${colors.yellow};
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.85;
  }
`;

export const SyntaxSection = styled.section`
  max-width: 72rem;
  margin: 0 auto;
  padding: 0 1.5rem 3.5rem;
  width: 100%;
`;

export const SyntaxLead = styled.p`
  font-size: 1.0625rem;
  line-height: 1.7;
  color: ${colors.textMuted};
  max-width: 48rem;
  margin-bottom: 1.25rem;
`;

export const KeywordTableWrap = styled.div`
  margin-top: 0.5rem;
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid ${colors.borderSubtle};
  background: ${colors.surface};
`;

export const KeywordTable = styled.table`
  width: 100%;
  min-width: 520px;
  border-collapse: collapse;
  font-size: 0.875rem;

  tbody tr:nth-child(even) td {
    background: rgba(255, 255, 255, 0.02);
  }
`;

export const KwThead = styled.thead`
  background: ${colors.surfaceRaised};
`;

export const KwTh = styled.th`
  text-align: left;
  padding: 0.75rem 1rem;
  font-weight: 800;
  font-size: 0.6875rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${colors.yellow};
  border-bottom: 1px solid ${colors.borderSubtle};
`;

export const KwTdBoleh = styled.td`
  padding: 0.65rem 1rem;
  vertical-align: top;
  line-height: 1.55;
  border-bottom: 1px solid ${colors.borderSubtle};
  ${mono}
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${colors.syntax.keyword};
  white-space: nowrap;
`;

export const KwTdConv = styled.td`
  padding: 0.65rem 1rem;
  vertical-align: top;
  line-height: 1.55;
  border-bottom: 1px solid ${colors.borderSubtle};
  ${mono}
  font-size: 0.8125rem;
  color: ${colors.textOnDark};
`;

export const KwTdMean = styled.td`
  padding: 0.65rem 1rem;
  vertical-align: top;
  line-height: 1.55;
  border-bottom: 1px solid ${colors.borderSubtle};
  font-size: 0.875rem;
  color: ${colors.textMuted};
`;

export const CtaBand = styled.section`
  max-width: 72rem;
  margin: 0 auto;
  padding: 0 1.5rem 4rem;
  width: 100%;
`;

export const CtaInner = styled.div`
  text-align: center;
  padding: 3rem 1.5rem;
  border-radius: 20px;
  border: 1px solid ${colors.borderSubtle};
  background: linear-gradient(160deg, ${colors.blue} 0%, #0a0a4a 50%, ${colors.surface} 100%);
`;

export const CtaKicker = styled.p`
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.18em;
  color: ${colors.yellow};
  margin-bottom: 0.75rem;
`;

export const CtaTitle = styled.h2`
  font-size: clamp(1.5rem, 4vw, 2rem);
  font-weight: 800;
  line-height: 1.25;
  color: ${colors.textOnDark};
  margin-bottom: 1.5rem;
`;

export const CtaBtn = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.85rem 1.75rem;
  border-radius: 999px;
  font-size: 0.875rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  background: ${colors.yellow};
  color: ${colors.blue};
  transition: transform 0.12s ease, box-shadow 0.12s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(254, 209, 0, 0.4);
  }
`;

export const CtaActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem;
`;

export const CtaBtnAlt = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 1.5rem;
  border-radius: 999px;
  font-size: 0.875rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  background: transparent;
  color: ${colors.textOnDark};
  border: 1px solid ${colors.borderSubtle};
  transition: transform 0.12s ease, background 0.12s ease;

  &:hover {
    transform: translateY(-2px);
    background: rgba(255, 255, 255, 0.06);
  }
`;

export const Footer = styled.footer`
  margin-top: auto;
  padding: 2.5rem 1.5rem;
  border-top: 1px solid ${colors.borderSubtle};
  text-align: center;
`;

export const FooterLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

export const FooterNote = styled.p`
  font-size: 0.8125rem;
  line-height: 1.6;
  color: ${colors.textMuted};

  a {
    color: ${colors.yellow};
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export const FooterCopy = styled.p`
  margin-top: 0.75rem;
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${colors.textMuted};
  opacity: 0.8;
`;
